import type { PapierkorbEintrag } from "@gui-creator/gemeinsam";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveRestore } from "lucide-react";
import { schluessel } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { useDarf } from "../huelle/konto.ts";
import { useHinweis } from "../ui/Hinweise.tsx";
import "./seiten.css";

const ART: Record<PapierkorbEintrag["art"], string> = { plugin: "Plugin", workspace: "Workspace", ui: "UI" };
const datum = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

export function Papierkorb() {
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const darf = useDarf("struktur");
  const liste = useQuery({ queryKey: ["papierkorb"], queryFn: () => api<PapierkorbEintrag[]>("/api/papierkorb") });

  const holen = async (e: PapierkorbEintrag) => {
    try {
      await api(`/api/papierkorb/${e.art}/${e.id}`, { methode: "POST" });
      await Promise.all([qc.invalidateQueries({ queryKey: ["papierkorb"] }), qc.invalidateQueries({ queryKey: schluessel.struktur })]);
      hinweis({ text: `„${e.name ?? e.schluessel}“ ist wiederhergestellt.` });
    } catch (f) {
      hinweis({ text: (f as Error).message, art: "fehler" });
    }
  };

  return (
    <main className="seite">
      <header className="seite-kopf">
        <h1>Papierkorb</h1>
        <p className="leise">Gelöschtes bleibt 30 Tage und wird dann endgültig entfernt.</p>
      </header>
      {liste.isPending && <p className="leise">Lädt …</p>}
      {liste.isError && <p className="feld-fehler">{liste.error.message}</p>}
      {liste.data?.length === 0 && <p className="seite-leer">Der Papierkorb ist leer.</p>}
      {!!liste.data?.length && (
        <table className="tabelle">
          <thead>
            <tr>
              <th>Art</th>
              <th>Pfad</th>
              <th>Gelöscht</th>
              <th aria-label="Aktion" />
            </tr>
          </thead>
          <tbody>
            {liste.data.map((e) => (
              <tr key={`${e.art}-${e.id}`}>
                <td>{ART[e.art]}</td>
                <td>
                  <span className="mono">{e.pfad}</span>
                  {e.name && e.name !== e.schluessel && <span className="leise"> · {e.name}</span>}
                </td>
                <td className="zahlen">{datum.format(new Date(e.geloeschtAm))}</td>
                <td className="tabelle-aktion">
                  {darf && (
                    <button type="button" className="knopf" onClick={() => void holen(e)}>
                      <ArchiveRestore size={14} aria-hidden="true" /> Wiederherstellen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
